import { useMemo } from 'react'
import { COMPANY_LEVEL_VALUE } from '@/components/form/project-select-field'
import { hasProjectLevelRole } from '@/lib/rbac'

type RefineableProjectSchema<TSchemaWithProjectRequirement> = {
  refine(
    check: (values: { idProject?: string }) => unknown,
    params: {
      message: string
      path: Array<string | number | symbol>
    }
  ): TSchemaWithProjectRequirement
}

type ProjectWithId = {
  id: string
}

type Props<TSchemaWithProjectRequirement> = {
  schema: RefineableProjectSchema<TSchemaWithProjectRequirement>
  rolesInCompany: string[]
  isCompanyLevelUser: boolean
  idProjectFromUrl: string | null
  projects: ProjectWithId[]
}

const withRequiredProjectSchema = <TSchemaWithProjectRequirement>(
  schema: RefineableProjectSchema<TSchemaWithProjectRequirement>,
  isProjectSelectionRequired: boolean
) => {
  return schema.refine(
    (values) =>
      !isProjectSelectionRequired ||
      Boolean(values.idProject && values.idProject !== COMPANY_LEVEL_VALUE),
    {
      message: 'Project is required',
      path: ['idProject'],
    }
  )
}

export const useCreateFormProjectSelection = <TSchemaWithProjectRequirement>({
  schema,
  rolesInCompany,
  isCompanyLevelUser,
  idProjectFromUrl,
  projects,
}: Props<TSchemaWithProjectRequirement>) => {
  const isProjectSelectionRequired = useMemo(
    () => hasProjectLevelRole(rolesInCompany),
    [rolesInCompany]
  )

  const isProjectOptional = useMemo(
    () => isCompanyLevelUser && !isProjectSelectionRequired,
    [isCompanyLevelUser, isProjectSelectionRequired]
  )

  const initialIdProject = useMemo(() => {
    if (!idProjectFromUrl) {
      return isProjectOptional ? COMPANY_LEVEL_VALUE : undefined
    }

    const isValidProject = projects.some(
      (project) => project.id === idProjectFromUrl
    )
    return isValidProject ? idProjectFromUrl : undefined
  }, [idProjectFromUrl, isProjectOptional, projects])

  const schemaWithProjectRequirement = useMemo(
    () => withRequiredProjectSchema(schema, isProjectSelectionRequired),
    [schema, isProjectSelectionRequired]
  )

  return {
    initialIdProject,
    isProjectOptional,
    isProjectSelectionRequired,
    schemaWithProjectRequirement,
  }
}
